/**
 * Page cache: show cached content immediately, then fetch in background and
 * update only when the fetched data is different.
 *
 * - Cache keys are scoped by page and user (e.g. pageCache:home:uid).
 * - Stored values are normalized (Firestore Timestamps → serializable form).
 * - On read, Timestamp-like objects are restored so existing component code works.
 * - Uses IndexedDB (via localforage) for larger storage; localStorage for sync reads.
 */

import localforage from 'localforage';

const pageCacheStore = localforage.createInstance({
  name: 'victorise-page-cache',
  storeName: 'cache',
});

const CACHE_PREFIX = 'pageCache:';
const CACHE_VERSION = 1;
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours - still show stale while refetching

function isFirestoreTimestamp(v) {
  return v && typeof v === 'object' && typeof v.toDate === 'function' && typeof v.seconds === 'number';
}

function serializeTimestamp(v) {
  if (!v) return v;
  if (isFirestoreTimestamp(v)) return { _ts: v.seconds };
  if (v instanceof Date) return { _ts: Math.floor(v.getTime() / 1000) };
  if (typeof v === 'object' && typeof v.seconds === 'number') return { _ts: v.seconds };
  return v;
}

function deserializeTimestamp(v) {
  if (!v || typeof v !== 'object' || typeof v._ts !== 'number') return v;
  const seconds = v._ts;
  return {
    seconds,
    toDate: () => new Date(seconds * 1000),
    toMillis: () => seconds * 1000,
  };
}

export function normalizeForStorage(obj, seen = new Set()) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (seen.has(obj)) return null;
  if (Array.isArray(obj)) {
    seen.add(obj);
    const out = obj.map((item) => normalizeForStorage(item, seen));
    seen.delete(obj);
    return out;
  }
  if (isFirestoreTimestamp(obj)) return serializeTimestamp(obj);
  if (obj instanceof Date) return serializeTimestamp(obj);
  if (typeof obj.seconds === 'number' && typeof obj.toDate === 'function') return serializeTimestamp(obj);
  seen.add(obj);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = normalizeForStorage(v, seen);
  }
  seen.delete(obj);
  return out;
}

export function denormalizeFromStorage(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(denormalizeFromStorage);
  if (typeof obj === 'object' && '_ts' in obj && Object.keys(obj).length === 1) {
    return deserializeTimestamp(obj);
  }
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = denormalizeFromStorage(v);
  }
  return out;
}

/**
 * @param {string} pageName - e.g. 'home', 'myRides', 'calendar', 'trainingPlans'
 * @param {string|null} userId - current user id (null for public data)
 * @returns {string} cache key
 */
export function cacheKey(pageName, userId = null) {
  const uid = userId === null || userId === undefined ? 'anon' : String(userId);
  return `${CACHE_PREFIX}${pageName}:${uid}`;
}

/**
 * Get cached value if present and not expired. Returns denormalized data (Timestamp-like restored).
 * @param {string} key - from cacheKey()
 * @returns {object|null} cached payload or null
 */
export function getCached(key) {
  try {
    const raw = window?.localStorage?.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CACHE_VERSION) return null;
    const age = Date.now() - (parsed.savedAtMs || 0);
    if (age > MAX_CACHE_AGE_MS) return null;
    const data = denormalizeFromStorage(parsed.data);
    return data;
  } catch {
    return null;
  }
}

/**
 * Get cached value if present, even if expired. Use for immediate display (stale-while-revalidate).
 * @param {string} key - from cacheKey()
 * @returns {object|null} cached payload or null
 */
export function getCachedStale(key) {
  try {
    const raw = window?.localStorage?.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CACHE_VERSION) return null;
    const data = denormalizeFromStorage(parsed.data);
    return data;
  } catch {
    return null;
  }
}

function buildCachePayload(data) {
  const normalized = normalizeForStorage(data);
  return {
    version: CACHE_VERSION,
    savedAtMs: Date.now(),
    data: normalized,
  };
}

/**
 * Save value to cache. Normalizes Timestamps for JSON.
 * Writes to both localStorage (for sync reads) and IndexedDB (for larger storage).
 * @param {string} key - from cacheKey()
 * @param {object} data - payload to store
 */
export function setCached(key, data) {
  const payload = buildCachePayload(data);
  const raw = JSON.stringify(payload);
  try {
    window?.localStorage?.setItem(key, raw);
  } catch (e) {
    if (e?.name === 'QuotaExceededError') {
      try {
        clearExpiredPageCaches();
        window?.localStorage?.setItem(key, raw);
      } catch {
        // ignore - will rely on IndexedDB
      }
    }
  }
  // Always write to IndexedDB (async, fire-and-forget) for larger storage
  pageCacheStore.setItem(key, payload).catch(() => {});
}

/**
 * Get cached value from IndexedDB (async). Use when localStorage may be empty but IndexedDB has data.
 * @param {string} key - from cacheKey()
 * @param {boolean} allowStale - if true, return even when expired
 * @returns {Promise<object|null>}
 */
export async function getCachedAsync(key, allowStale = false) {
  try {
    const parsed = await pageCacheStore.getItem(key);
    if (!parsed) return null;
    if (parsed?.version !== CACHE_VERSION) return null;
    if (!allowStale) {
      const age = Date.now() - (parsed.savedAtMs || 0);
      if (age > MAX_CACHE_AGE_MS) return null;
    }
    const data = denormalizeFromStorage(parsed.data);
    return data;
  } catch {
    return null;
  }
}

/**
 * Get cached value (stale ok) from IndexedDB. Fallback when localStorage is empty.
 * @param {string} key - from cacheKey()
 * @returns {Promise<object|null>}
 */
export async function getCachedStaleAsync(key) {
  return getCachedAsync(key, true);
}

/**
 * Build a stable fingerprint for "is data different?".
 * Uses length + sorted ids for arrays of items with .id; otherwise JSON string of normalized data.
 */
export function fingerprint(data) {
  if (data === null || data === undefined) return '';
  if (Array.isArray(data)) {
    const ids = data.map((i) => (i && (i.id ?? i.docId ?? i.runId)) ?? '').filter(Boolean);
    return `${data.length}:${[...ids].sort().join(',')}`;
  }
  if (typeof data === 'object') {
    const normalized = normalizeForStorage(data);
    return JSON.stringify(normalized);
  }
  return String(data);
}

/**
 * Combine multiple fingerprints (e.g. runs + events) for one page.
 */
export function fingerprintMulti(parts) {
  return parts.map((p) => fingerprint(p)).join('|');
}

/**
 * Remove cache entries older than MAX_CACHE_AGE_MS to free quota.
 */
function clearExpiredPageCaches() {
  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => {
      try {
        const raw = window.localStorage.getItem(k);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed?.savedAtMs && Date.now() - parsed.savedAtMs > MAX_CACHE_AGE_MS) {
          window.localStorage.removeItem(k);
        }
      } catch {
        // ignore
      }
    });
  } catch {
    // ignore
  }
}
